import type { CSSProperties, Ref } from "react";
import { useTranslate } from "@/lib/i18n";
import type {
  KitchenBoardPastPlate,
  KitchenBoardPlate,
  KitchenWorkflow,
} from "@/lib/api/types";
import type { PlateLane } from "../lib/lateness";
import { voidLane } from "../lib/voids";
import { boardLanes, laneOfState } from "../lib/workflow";
import { PaperKitchenLane, type KitchenCommand } from "./PaperKitchen";

/**
 * THE KITCHEN'S LANES, ONE COMPONENT FOR BOTH SURFACES (plan §4.10, D170).
 *
 * The wall board (`/kitchen`) and the floor's Kitchen tab draw their lanes
 * through this, so the two cannot drift: the lanes come from the store's
 * workflow (`boardLanes`) — «جديد / في التحضير / جاهز» on a two-tap screen,
 * «في التحضير / جاهز» on one-tap and paper + pass — and each plate goes to
 * the lane that holds its state. A voided plate (B5) goes to the lane its
 * stamps say, and a «مدفوع» card (T7) to READY when it was ready, else to the
 * cooking lane. Below the lanes, one polite live region carries the lane
 * counts (UX-round D49).
 */
export function KitchenLanes({
  workflow,
  plates,
  voided = [],
  paid = [],
  now,
  busyItemIds,
  canPrepare,
  canServe,
  isCapped = false,
  lanesRef,
  style,
  testId,
  newKeys,
  onRun,
  onHidePaid,
}: {
  workflow: KitchenWorkflow;
  plates: readonly KitchenBoardPlate[];
  voided?: readonly KitchenBoardPastPlate[];
  paid?: readonly KitchenBoardPastPlate[];
  now: number;
  busyItemIds: ReadonlySet<string>;
  canPrepare: boolean;
  canServe: boolean;
  isCapped?: boolean;
  lanesRef?: Ref<HTMLDivElement>;
  style?: CSSProperties;
  testId: string;
  newKeys?: ReadonlySet<string>;
  onRun: (plates: KitchenBoardPlate[], type: KitchenCommand) => Promise<boolean | void>;
  onHidePaid?: (plateIds: string[]) => void;
}) {
  const t = useTranslate();
  const lanes = boardLanes(workflow);
  if (lanes.length === 0) return null;
  const label = (lane: PlateLane) =>
    t((m) =>
      lane === "QUEUED" ? m.kitchen.laneQueued : lane === "PREPARING" ? m.kitchen.lanePreparing : m.kitchen.laneReady,
    );
  const fallbackLane = lanes[0].id;
  const voidedIn = (lane: PlateLane) =>
    voided.filter((plate) => (laneOfState(workflow, voidLane(plate)) ?? fallbackLane) === lane);
  const paidIn = (lane: PlateLane) =>
    paid.filter((plate) => (plate.readyAt ? "READY" : laneOfState(workflow, "PREPARING")) === lane);
  const platesIn = (states: readonly string[]) =>
    plates.filter((plate) => states.includes(plate.fulfillmentState));

  return (
    <>
      {/* Lanes side by side from 1024px, stacked below. Not a table and not one
          scrolling list: a cook reads the board by COLUMN — "what is waiting",
          "what is on", "what is up". `items-stretch` under a cap makes the
          lanes equal boxes, each scrolling by itself (UX-round D48). */}
      <div
        ref={lanesRef}
        data-testid={testId}
        data-ui="kitchen-lanes"
        data-lane-count={lanes.length}
        data-capped={isCapped ? "" : undefined}
        className={isCapped ? "grid items-stretch" : "grid items-start"}
        style={style}
      >
        {lanes.map((lane) => (
          <PaperKitchenLane
            key={lane.id}
            lane={lane.id}
            label={label(lane.id)}
            emptyLabel={t((m) => m.kitchen.laneEmpty)}
            plates={platesIn(lane.states)}
            voided={voidedIn(lane.id)}
            paid={paidIn(lane.id)}
            workflow={workflow}
            now={now}
            busyItemIds={busyItemIds}
            canPrepare={canPrepare}
            canServe={canServe}
            isCapped={isCapped}
            newKeys={newKeys}
            onRun={onRun}
            onHidePaid={onHidePaid}
          />
        ))}
      </div>
      {/* ARRIVAL AND BUMP, ANNOUNCED (UX-round D49): one polite region with
          the lane counts — the same numbers a sighted cook reads off the lane
          headings — never one region per card. */}
      {plates.length > 0 ? (
        <p role="status" aria-live="polite" className="sr-only">
          {lanes.map((lane) => `${label(lane.id)} ${platesIn(lane.states).length}`).join(" · ")}
        </p>
      ) : null}
    </>
  );
}
